import React from "react";
import { Link } from "react-router-dom";
import { MousePointerClick, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

/**
 * One-click Vision-Language Model fine-tuning callout.
 * Placed on all product pages and the RCM blog post.
 */
export const FineTuningCallout = ({ formName = "your form" }) => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl border border-brand-200/40 bg-gradient-to-br from-brand-50 via-white to-white p-8 shadow-lg shadow-brand/5 sm:p-12">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Sparkles className="h-3.5 w-3.5" /> One-click VLM fine-tuning
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Fine-tune a vision-language model at the click of a button
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Hewto.ai provides vision-language model fine-tuning at the click of a button. Upload a
              handful of labeled samples of {formName} — or any new form variant, payer template, or
              document type you handle — and Hewto.ai fine-tunes a purpose-built VLM for it. No ML
              team, no training pipelines, no infrastructure to manage.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
              {[
                "Upload samples, click fine-tune, ship",
                "No ML engineers or training pipelines required",
                "Custom accuracy on your document mix",
                "Ready to run in minutes, not weeks",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                to="/contact-sales"
                data-testid="fine-tuning-cta"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
              >
                Talk to us about fine-tuning <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
                <MousePointerClick className="h-3.5 w-3.5 text-brand" /> Fine-tune workflow
              </div>
              <ol className="mt-4 space-y-4 text-sm">
                {[
                  { n: "01", t: "Upload labeled samples", d: "Drop in 20–200 examples of your target form." },
                  { n: "02", t: "Click Fine-tune", d: "Hewto.ai kicks off a fine-tuning run — no config, no code." },
                  { n: "03", t: "Ship the model", d: "Your custom VLM goes live behind the same API endpoint." },
                ].map((s) => (
                  <li key={s.n} className="flex items-start gap-3">
                    <span className="font-mono text-xs font-semibold text-brand-600">{s.n}</span>
                    <span>
                      <span className="block font-semibold text-slate-900">{s.t}</span>
                      <span className="block text-slate-500">{s.d}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

export default FineTuningCallout;
